import { Provider, ApiModel } from "@/hooks/use-clerk-metadata";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import { UserResource } from "@clerk/types";
import { extractWebAppId, extractSheetId } from "./clerk-url-utils";
import { interceptClerkWriteIfSimulating } from "@/dev/types/inspector-types";

export interface OnboardingTransferData {
  apiKeys: {
    [key in Provider]?: {
      key: string;
      provider: Provider;
      validated: boolean;
    };
  };
  accountInfo?: {
    accountCID: string;
    accountName: string;
    webAppUrl?: string;
    sheetUrl?: string;
    businessMode?: "ecomm" | "leadgen";
  };
  onboardingProgress?: {
    stage: string;
    completedSteps: string[];
    lastVisited: string;
  };
}

/**
 * Collects all onboarding data from localStorage and context that should be transferred to a new user account
 */
export function collectOnboardingDataForTransfer(): OnboardingTransferData {
  const transferData: OnboardingTransferData = {
    apiKeys: {},
  };

  // Collect API keys from localStorage (guest mode)
  const providers: Provider[] = ["openai", "anthropic", "gemini"];

  providers.forEach((provider) => {
    const key = localStorage.getItem(`guest_${provider}_api_key`);
    if (key) {
      transferData.apiKeys[provider] = {
        key,
        provider,
        validated: true, // Assume they were validated during onboarding
      };
    }
  });

  // Collect any temporary account information from localStorage
  const tempWebAppUrl = localStorage.getItem("temp_web_app_url");
  const tempSheetUrl = localStorage.getItem("temp_sheet_url");
  const tempAccountData = localStorage.getItem("temp_account_data");

  if (tempAccountData) {
    try {
      const accountInfo = JSON.parse(tempAccountData);

      // Compress URLs before saving to reduce metadata size
      const webAppUrl = tempWebAppUrl || accountInfo.webAppUrl;
      const compressedWebAppUrl = extractWebAppId(webAppUrl) || webAppUrl;
      const compressedSheetUrl =
        extractSheetId(tempSheetUrl) || tempSheetUrl;

      transferData.accountInfo = {
        accountCID: accountInfo.accountCID,
        accountName: accountInfo.accountName,
        webAppUrl: compressedWebAppUrl,
        sheetUrl: compressedSheetUrl,
        businessMode: accountInfo.businessMode || "ecomm",
      };

      console.log("[TRANSFER] Compressed URLs:", {
        webAppUrl: compressedWebAppUrl,
        sheetUrl: compressedSheetUrl,
      });
    } catch (error) {
      console.warn("Failed to parse temp account data:", error);
    }
  }

  // Collect onboarding progress
  const onboardingProgress = localStorage.getItem("onboarding_progress");
  if (onboardingProgress) {
    try {
      transferData.onboardingProgress = JSON.parse(onboardingProgress);
    } catch (error) {
      console.warn("Failed to parse onboarding progress:", error);
    }
  }

  console.log(
    "🔄 [TRANSFER] Collected onboarding data for transfer:",
    transferData,
  );
  return transferData;
}

/**
 * Transfers collected onboarding data to the user's Clerk metadata
 */
export async function transferOnboardingDataToUser(
  user: UserResource,
  transferData: OnboardingTransferData,
): Promise<void> {
  console.log("🚀 [TRANSFER] Starting transfer to user metadata:", user.id);

  try {
    // Prepare the metadata update
    const currentMetadata = user.unsafeMetadata || {};
    const updateData: any = { ...currentMetadata };

    // Transfer API keys
    if (Object.keys(transferData.apiKeys).length > 0) {
      const apiModels: { [key in Provider]?: ApiModel } = {};

      Object.entries(transferData.apiKeys).forEach(([provider, apiData]) => {
        if (apiData) {
          apiModels[provider as Provider] = {
            key: apiData.key,
            validated: apiData.validated,
          };
        }
      });

      updateData.apiModels = {
        ...((currentMetadata.apiModels as { [key in Provider]?: ApiModel }) ||
          {}),
        ...apiModels,
      };

      console.log(
        "✅ [TRANSFER] API keys prepared for transfer:",
        Object.keys(apiModels),
      );
    }

    // Transfer account information
    if (transferData.accountInfo) {
      // Store account information that can be used later to set up the account
      updateData.pendingAccountSetup = transferData.accountInfo;
      console.log(
        "✅ [TRANSFER] Account info prepared for transfer:",
        transferData.accountInfo.accountCID,
      );
    }

    // Transfer onboarding progress
    if (transferData.onboardingProgress) {
      updateData.onboardingStage =
        transferData.onboardingProgress.stage || "COMPLETE";
      updateData.onboardingCompletedSteps =
        transferData.onboardingProgress.completedSteps || [];
      console.log("✅ [TRANSFER] Onboarding progress prepared for transfer");
    }

    // Check if simulation mode is enabled - intercept instead of updating Clerk
    if (interceptClerkWriteIfSimulating(updateData)) {
      console.log(
        "🎭 [SIMULATION] Intercepted transfer - data saved to inspector, not Clerk",
      );
      // Don't clear localStorage in simulation mode so we can repeat tests
      return;
    }

    // Update user metadata
    await user.update({
      unsafeMetadata: updateData,
    });

    console.log(
      "🎉 [TRANSFER] Successfully transferred onboarding data to user metadata",
    );

    // Clear localStorage after successful transfer
    clearOnboardingDataFromLocalStorage();
  } catch (error) {
    console.error("❌ [TRANSFER] Failed to transfer onboarding data:", error);
    throw new Error("Failed to transfer onboarding data to user account");
  }
}

/**
 * Clears onboarding data from localStorage after successful transfer
 */
export function clearOnboardingDataFromLocalStorage(): void {
  console.log("🧹 [TRANSFER] Clearing onboarding data from localStorage");

  // Clear API keys
  ["openai", "anthropic", "gemini"].forEach((provider) => {
    localStorage.removeItem(`guest_${provider}_api_key`);
  });

  // Clear temporary account data
  localStorage.removeItem("temp_web_app_url");
  localStorage.removeItem("temp_sheet_url");
  localStorage.removeItem("temp_account_data");
  localStorage.removeItem("onboarding_progress");

  console.log("✅ [TRANSFER] Onboarding data cleared from localStorage");
}

/**
 * Hook that automatically handles onboarding data transfer when a user signs up
 */
export function useOnboardingDataTransfer() {
  const transferDataOnSignUp = async (user: UserResource): Promise<void> => {
    try {
      const transferData = collectOnboardingDataForTransfer();

      // Only transfer if there's meaningful data to transfer
      const hasApiKeys = Object.keys(transferData.apiKeys).length > 0;
      const hasAccountInfo = !!transferData.accountInfo;
      const hasProgress = !!transferData.onboardingProgress;

      if (hasApiKeys || hasAccountInfo || hasProgress) {
        console.log(
          "🔄 [TRANSFER] Detected onboarding data to transfer for new user",
        );
        await transferOnboardingDataToUser(user, transferData);
      } else {
        console.log("ℹ️ [TRANSFER] No onboarding data found to transfer");
      }
    } catch (error) {
      console.error(
        "❌ [TRANSFER] Failed to transfer onboarding data on signup:",
        error,
      );
      // Don't throw error here to avoid breaking signup flow
    }
  };

  return {
    transferDataOnSignUp,
  };
}
